import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { CONTACT } from "@/data/site";
import { SERVICES } from "@/data/services";

const COMPANY_ITEMS = [
  { label: "About", to: "/about", description: "Vision, mission & values" },
  { label: "Founder", to: "/founder", description: "A message from T G Dey" },
  { label: "Leadership", to: "/leadership", description: "Our senior team" },
];

const PORTFOLIO_ITEMS = [
  { label: "Projects", to: "/projects", description: "Selected case studies" },
  { label: "Industries", to: "/industries", description: "Sectors we serve" },
  { label: "Partners", to: "/partners", description: "Brands we build with" },
];

const TOP_NAV = [
  { label: "Home", to: "/", kind: "link" },
  { label: "Company", kind: "dropdown", key: "company", items: COMPANY_ITEMS },
  { label: "Services", to: "/services", kind: "mega" },
  { label: "Portfolio", kind: "dropdown", key: "portfolio", items: PORTFOLIO_ITEMS },
  { label: "Careers", to: "/careers", kind: "link" },
  { label: "Contact", to: "/contact", kind: "link" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileGroup, setMobileGroup] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActiveDropdown(null);
    setMobileGroup(null);
  }, [pathname]);

  // Permanent white navbar with dark text — readable on every surface
  const linkBase = "text-slate-700 hover:text-navy";
  const linkActive = "text-navy underline-gold";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200/80 shadow-md"
          : "bg-white/90 backdrop-blur-xl border-b border-slate-200/60"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo" aria-label="NEPHRC - Engineering Tomorrow">
          <img
            src="/brand/nephrc-logo-transparent.png"
            alt="NEPHRC - North East Projects, Hospitality & Retails Consultancy"
            className="h-12 sm:h-14 w-auto select-none"
            draggable="false"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" onMouseLeave={() => setActiveDropdown(null)}>
          {TOP_NAV.map((entry) => {
            const linkClass = ({ isActive }) =>
              `px-3 py-2 text-sm font-medium tracking-wide inline-flex items-center gap-1 transition-colors ${linkBase} ${
                isActive ? linkActive : ""
              }`;

            if (entry.kind === "link") {
              return (
                <NavLink
                  key={entry.label}
                  to={entry.to}
                  end={entry.to === "/"}
                  data-testid={`nav-link-${entry.label.toLowerCase()}`}
                  className={linkClass}
                  onMouseEnter={() => setActiveDropdown(null)}
                >
                  {entry.label}
                </NavLink>
              );
            }

            if (entry.kind === "mega") {
              return (
                <div
                  key={entry.label}
                  onMouseEnter={() => setActiveDropdown("services")}
                  className="relative"
                >
                  <NavLink
                    to={entry.to}
                    data-testid={`nav-link-${entry.label.toLowerCase()}`}
                    className={linkClass}
                  >
                    {entry.label} <ChevronDown size={14} />
                  </NavLink>
                </div>
              );
            }

            return (
              <div
                key={entry.label}
                onMouseEnter={() => setActiveDropdown(entry.key)}
                className="relative"
              >
                <button
                  type="button"
                  data-testid={`nav-link-${entry.label.toLowerCase()}`}
                  className={`px-3 py-2 text-sm font-medium tracking-wide inline-flex items-center gap-1 transition-colors ${linkBase}`}
                >
                  {entry.label} <ChevronDown size={14} />
                </button>
              </div>
            );
          })}

          {/* Mega menu panel for Services */}
          <AnimatePresence>
            {activeDropdown === "services" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[820px] bg-white border border-slate-200 shadow-2xl"
                data-testid="services-mega-menu"
              >
                <div className="p-6 grid grid-cols-2 gap-x-6 gap-y-1">
                  {SERVICES.map((s) => {
                    const Icon = s.icon;
                    return (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        className="group flex items-start gap-3 p-3 hover:bg-mist transition-colors"
                        data-testid={`mega-service-${s.slug}`}
                      >
                        <div className="w-9 h-9 grid place-items-center border border-slate-200 text-emerald-brand group-hover:border-emerald-brand transition-colors">
                          <Icon size={18} strokeWidth={1.5} />
                        </div>
                        <div>
                          <div className="font-display font-semibold text-navy text-sm">{s.title}</div>
                          <div className="text-xs text-slate-500 line-clamp-1">{s.short}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Dropdown panel for Company/Portfolio */}
          <AnimatePresence>
            {(activeDropdown === "company" || activeDropdown === "portfolio") && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full mt-2 w-72 bg-white border border-slate-200 shadow-2xl"
                style={{
                  left: activeDropdown === "company" ? "16%" : "62%",
                }}
                data-testid={`${activeDropdown}-dropdown-menu`}
              >
                <div className="p-3">
                  {(activeDropdown === "company" ? COMPANY_ITEMS : PORTFOLIO_ITEMS).map((it) => (
                    <Link
                      key={it.to}
                      to={it.to}
                      data-testid={`dropdown-link-${it.label.toLowerCase()}`}
                      className="block p-3 hover:bg-mist transition-colors group"
                    >
                      <div className="font-display font-semibold text-navy text-sm group-hover:text-emerald-brand transition-colors">
                        {it.label}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{it.description}</div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <a
            href={`tel:${CONTACT.primaryPhone}`}
            data-testid="navbar-call-cta"
            className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-slate-700 hover:text-navy transition-colors"
          >
            <Phone size={14} />
            <span className="hidden xl:inline">{CONTACT.phones[0]}</span>
          </a>
          <Link
            to="/contact"
            data-testid="navbar-contact-cta"
            className="inline-flex items-center px-5 py-3 bg-navy text-white font-semibold text-xs tracking-wider uppercase hover:bg-emerald-brand transition-colors"
          >
            Start a Project
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2"
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="text-navy" /> : <Menu className="text-navy" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-white border-t border-slate-200 overflow-hidden"
            data-testid="mobile-nav"
          >
            <div className="container-x py-6 flex flex-col gap-1">
              {TOP_NAV.map((entry) => {
                if (entry.kind === "link" || entry.kind === "mega") {
                  return (
                    <NavLink
                      key={entry.label}
                      to={entry.to}
                      end={entry.to === "/"}
                      data-testid={`mobile-nav-link-${entry.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `px-2 py-3 border-b border-slate-100 text-base font-medium ${
                          isActive ? "text-navy" : "text-slate-700"
                        }`
                      }
                    >
                      {entry.label}
                    </NavLink>
                  );
                }
                const isOpen = mobileGroup === entry.key;
                return (
                  <div key={entry.label} className="border-b border-slate-100">
                    <button
                      onClick={() => setMobileGroup(isOpen ? null : entry.key)}
                      data-testid={`mobile-nav-group-${entry.key}`}
                      className="w-full flex items-center justify-between px-2 py-3 text-base font-medium text-slate-700"
                    >
                      {entry.label}
                      <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="pl-4 pb-3 space-y-1">
                        {entry.items.map((it) => (
                          <NavLink
                            key={it.to}
                            to={it.to}
                            data-testid={`mobile-nav-link-${it.label.toLowerCase()}`}
                            className="block px-2 py-2 text-sm text-slate-600 hover:text-navy"
                          >
                            {it.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <Link
                to="/contact"
                className="mt-4 inline-flex items-center justify-center px-5 py-3 bg-navy text-white font-semibold text-xs tracking-wider uppercase"
                data-testid="mobile-nav-cta"
              >
                Start a Project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
